import { Transaction, SystemProgram, type Connection } from '@solana/web3.js';
import type { Adapter, SignerWalletAdapter } from '@solana/wallet-adapter-base';
import { CWY_IS_MAINNET } from '../config/cwyConfig';
import { catalogEntryForAdapterName } from '../config/walletCatalog';

const NETWORK_CHECK_WALLETS = new Set(['Brave', 'Nightly']);

function walletNetworkInstructions(adapterName: string): string {
  const base = CWY_IS_MAINNET
    ? 'This app uses Solana Mainnet, but your wallet is on Devnet.'
    : 'This app uses Solana Devnet, but your wallet is on Mainnet.';

  const entry = catalogEntryForAdapterName(adapterName as never);
  const label = entry?.label ?? adapterName;

  if (adapterName === 'Brave') {
    return (
      `${base}\n\nTo fix:\n` +
      '1. Open Brave → brave://settings/wallet\n' +
      `2. Under "Default Solana" network, switch to ${CWY_IS_MAINNET ? 'Solana Mainnet' : 'Solana Devnet'}\n` +
      '3. Reload this page and reconnect'
    );
  }

  return (
    `${base}\n\nTo fix:\n` +
    `1. Open ${label} extension → Settings → Network\n` +
    `2. Switch to ${CWY_IS_MAINNET ? 'Mainnet Beta' : 'Devnet'}\n` +
    '3. Reload this page and reconnect'
  );
}

/**
 * Soft network check for Brave/Nightly (they enforce cluster at sign time).
 *
 * - Clear network mismatch → throw with instructions (hard fail)
 * - User declines the probe / wallet rejects 0-lamport tx → skip (do not block connect)
 * - Other errors → skip (ownership sign + deposit will surface real problems)
 *
 * Phantom/Solflare/Backpack skip this entirely.
 */
export async function validateWalletNetwork(
  adapter: Adapter,
  connection: Connection
): Promise<void> {
  if (!NETWORK_CHECK_WALLETS.has(adapter.name)) return;
  if (!adapter.publicKey) return;

  try {
    const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
    const tx = new Transaction({
      blockhash,
      lastValidBlockHeight,
      feePayer: adapter.publicKey,
    });
    tx.add(
      SystemProgram.transfer({
        fromPubkey: adapter.publicKey,
        toPubkey: adapter.publicKey,
        lamports: 0,
      })
    );

    if ('signTransaction' in adapter) {
      await (adapter as unknown as SignerWalletAdapter).signTransaction!(tx);
    }
  } catch (error) {
    const msg = ((error as Error)?.message ?? '').toLowerCase();

    const isNetworkError =
      msg.includes('network mismatch') ||
      msg.includes('wrong network') ||
      msg.includes('switch to the correct network') ||
      (msg.includes('devnet') && msg.includes('mainnet')) ||
      msg.includes('cluster mismatch');

    if (isNetworkError) {
      throw new Error(walletNetworkInstructions(adapter.name));
    }

    // User rejection / unsupported 0-lamport / generic sign errors must not block linking.
    // Deposit/send will re-check with a real transaction.
  }
}

/**
 * Check if an error from a transaction signing is a network mismatch.
 * Returns a user-friendly message or null.
 */
export function formatNetworkMismatchError(error: unknown, walletProvider?: string): string | null {
  const msg = ((error as Error)?.message ?? '').toLowerCase();
  const isNetworkError =
    msg.includes('network mismatch') ||
    msg.includes('switch to the correct network') ||
    (msg.includes('devnet') && msg.includes('mainnet')) ||
    msg.includes('wrong network');

  if (!isNetworkError) return null;

  const label = walletProvider ?? 'your wallet';
  return (
    `${label} is set to the wrong Solana network. ` +
    `Open your wallet extension → Settings → Network → ` +
    `switch to ${CWY_IS_MAINNET ? 'Mainnet Beta' : 'Devnet'}, ` +
    'then reload and try again.'
  );
}
